<template>
  <div id="content" class="sections">
    <Head>
      <!-- Primary Meta Tags -->
      <title>{{ changeMetatagHtml($t("productCatalogue.metaTitle")) }}</title>
      <meta name="title" :content="changeMetatagHtml($t('productCatalogue.metaTitle'))">
      <meta name="description" :content="$t('productCatalogue.metaDescription')">
      <meta name="keywords" :content="$t('productCatalogue.metaKeywords')">

      <!-- Open Graph / Facebook -->
      <meta property="og:type" content="website">
      <meta property="og:url" content="https://komatools.com/">
      <meta property="og:title" :content="changeMetatagHtml($t('productCatalogue.metaTitle'))">
      <meta property="og:description" :content="$t('productCatalogue.metaDescription')">
      <meta property="og:image" content="https://komatools.com/img/komatoolsSocial.jpg">

      <!-- Twitter -->
      <meta property="twitter:card" content="summary_large_image">
      <meta property="twitter:url" content="https://komatools.com/">
      <meta property="twitter:title" :content="changeMetatagHtml($t('productCatalogue.metaTitle'))">
      <meta property="twitter:description" :content="$t('productCatalogue.metaDescription')">
      <meta property="twitter:image" content="https://komatools.com/img/komatoolsSocial.jpg">
    </Head>

    <div class="section light paddingBigger">
      <div>
        <!--
          Miga propia con <nav><ol>. No se reutiliza BreadcrumbComponent.vue:
          usa <v-breadcrumbs> de Vuetify, que no esta instalado en el proyecto.
        -->
        <nav class="catalogueBreadcrumb" :aria-label="$t('productCatalogue.breadcrumbRoot')">
          <ol>
            <li>
              <LocalizedLink :to="$t('routes.products')">{{ $t('productCatalogue.breadcrumbRoot') }}</LocalizedLink>
            </li>
            <li v-if="categorySlug">
              <LocalizedLink v-if="subCategorySlug" :to="`${$t('routes.products')}/${categorySlug}`">
                {{ categoryTitle }}
              </LocalizedLink>
              <span v-else>{{ categoryTitle }}</span>
            </li>
            <!-- el <span> no es decorativo: el CSS de la miga normaliza sobre el hijo, no sobre el <li> -->
            <li v-if="subCategorySlug"><span>{{ subCategoryTitle }}</span></li>
          </ol>
        </nav>

        <div class="catalogueHeader">
          <h1 class="title">{{ pageTitle }}</h1>
          <p class="subTitle">{{ pageSubtitle }}</p>
        </div>

        <div class="catalogue">
          <!--
            Las dos regiones cargan en paralelo y fallan por separado: si el
            arbol cae, la parrilla sigue viva y al reves. Nada de Promise.all.
          -->
          <CatalogueNav
              :categories="categories"
              :isLoading="isTreeLoading"
              :error="treeError"
              :activeCategory="categorySlug"
              :activeSubCategory="subCategorySlug"
              @retry="loadTree"
          />

          <div class="catalogueBody">
            <CatalogueCategoriesGrid
                v-if="isLanding"
                :items="gridItems"
                :isLoading="isGridLoading"
                :error="gridError"
                @retry="loadContent"
            />
            <CatalogueArticleGrid
                v-else
                :articles="articles"
                :isLoading="areArticlesLoading"
                :error="articlesError"
                @retry="loadContent"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Head } from '@vueuse/head'
import LocalizedLink from '@/components/LocalizedLink.vue'
import CatalogueNav from '@/components/catalogue/CatalogueNav.vue'
import CatalogueArticleGrid from '@/components/catalogue/CatalogueArticleGrid.vue'
import CatalogueCategoriesGrid from '@/components/catalogue/CatalogueCategoriesGrid.vue'
import getCategoriesAndSubcategories from '@/composables/getCategoriesAndSubcategories'
import getArticlesFromCategory from '@/composables/getArticlesFromCategory'
import getArticlesFromSubCategory from '@/composables/getArticlesFromSubCategory'
import getCategoriesGridArticles from '@/composables/getCategoriesGridArticles'

export default {
  name: 'Catalogue',
  components: { Head, LocalizedLink, CatalogueNav, CatalogueArticleGrid, CatalogueCategoriesGrid },
  props: ['category', 'subCategory'],
  setup(props) {
    const route = useRoute()
    const router = useRouter()
    const { t } = useI18n()

    const tree = getCategoriesAndSubcategories()
    const fromCategory = getArticlesFromCategory()
    const fromSubCategory = getArticlesFromSubCategory()
    const grid = getCategoriesGridArticles()

    // con params opcionales ausentes vue-router entrega cadena vacia, no undefined
    const categorySlug = computed(() => props.category || null)
    const subCategorySlug = computed(() => props.subCategory || null)
    const isLanding = computed(() => !categorySlug.value)

    const loadTree = () => tree.load()

    const loadContent = () => {
      if (subCategorySlug.value) return fromSubCategory.load(categorySlug.value, subCategorySlug.value)
      if (categorySlug.value) return fromCategory.load(categorySlug.value)

      return grid.load()
    }

    loadTree()
    loadContent()

    // vue-router reutiliza el componente cuando solo cambian los params
    watch(() => [props.category, props.subCategory], loadContent)

    /*
     * Los slugs de la URL quedan en el idioma anterior y el backend resuelve por
     * slug del idioma pedido, asi que un cambio de idioma dentro de una
     * categoria caeria en el estado vacio. Se reencamina a la raiz del catalogo:
     * traducir slugs exigiria un endpoint de equivalencias que no existe.
     */
    watch(() => route.params.locale, (locale) => {
      if (categorySlug.value || subCategorySlug.value) {
        router.replace('/' + locale + t('routes.products'))
        return
      }

      loadTree()
      loadContent()
    })

    const activeCategory = computed(() =>
        tree.categories.value.find((category) => category.slug === categorySlug.value) || null)

    const activeSubCategory = computed(() => {
      if (!activeCategory.value || !subCategorySlug.value) return null

      return activeCategory.value.subcategories
          .find((subcategory) => subcategory.slug === subCategorySlug.value) || null
    })

    // mientras el arbol no ha llegado se usa el slug, para no dejar el h1 vacio
    const readableSlug = (slug) => slug.split('-').join(' ')

    const categoryTitle = computed(() =>
        activeCategory.value ? activeCategory.value.name : readableSlug(categorySlug.value || ''))

    const subCategoryTitle = computed(() =>
        activeSubCategory.value ? activeSubCategory.value.name : readableSlug(subCategorySlug.value || ''))

    const pageTitle = computed(() => {
      if (subCategorySlug.value) return subCategoryTitle.value
      if (categorySlug.value) return categoryTitle.value

      return t('productCatalogue.title')
    })

    const articles = computed(() =>
        subCategorySlug.value ? fromSubCategory.articles.value : fromCategory.articles.value)

    const areArticlesLoading = computed(() =>
        subCategorySlug.value ? fromSubCategory.isLoading.value : fromCategory.isLoading.value)

    const articlesError = computed(() =>
        subCategorySlug.value ? fromSubCategory.error.value : fromCategory.error.value)

    /*
     * En la landing el subtitulo es el nombre de la categoria que se esta
     * enseñando: el titulo dice "catalogo" pero el cuerpo muestra una sola
     * categoria, y conviene que quede claro cual. El backend no lo devuelve en
     * el grid, asi que se deduce del slug compuesto y se busca en el arbol.
     */
    const gridCategoryName = computed(() => {
      if (!grid.items.value.length) return ''

      const slug = grid.items.value[0].slug.split('/')[0]
      const category = tree.categories.value.find((item) => item.slug === slug)

      return category ? category.name : ''
    })

    const pageSubtitle = computed(() => {
      if (isLanding.value) return gridCategoryName.value

      return t('productCatalogue.productCount', { count: articles.value.length })
    })

    /**
     * Desescapa entidades HTML de los metatags (mismo helper que el resto de
     * las secciones del proyecto).
     */
    const changeMetatagHtml = (html) => {
      const txt = document.createElement('textarea')
      txt.innerHTML = html

      return txt.value
    }

    return {
      categories: tree.categories,
      isTreeLoading: tree.isLoading,
      treeError: tree.error,
      gridItems: grid.items,
      isGridLoading: grid.isLoading,
      gridError: grid.error,
      articles,
      areArticlesLoading,
      articlesError,
      categorySlug,
      subCategorySlug,
      isLanding,
      categoryTitle,
      subCategoryTitle,
      pageTitle,
      pageSubtitle,
      loadTree,
      loadContent,
      changeMetatagHtml
    }
  }
}
</script>
